<template>
  <section class="activity-stage face-flip">
    <div class="cards-container">
      <figure
        v-for="(card, index) in currentRound.steps"
        :id="`drop-${index}`"
        :key="`card-${index}`"
        :class="`figure-type-${currentRound.steps.length}`"
      >
        <svg width="100%" height="100%">
          <image :xlink:href="card.images.letter" width="100%" height="100%"></image>
          <image
            class="mainImg"
            :xlink:href="card.images.main"
            width="100%"
            height="100%"
            y="28%"
          ></image>
          <image
            v-if="!isMatched[`m${index}`]"
            :xlink:href="card.images.puzzlePiece.missing"
            :width="`${card.images.puzzlePiece.width}%`"
            height="100%"
            :x="`${card.images.puzzlePiece.x}%`"
            :y="`${card.images.puzzlePiece.y}%`"
          ></image>
        </svg>
        <figcaption v-if="caption[`c${index}`]">
          <b v-text="card.name"></b>
        </figcaption>
      </figure>
    </div>
    <div class="puzzles-container">
      <figure
        v-for="(card, index) in currentRound.steps"
        :id="`drag-${index}`"
        :key="`puzzle-${index}`"
        :class="`figure-card figure-type-${currentRound.steps.length}`"
        :style="`order: ${card.order};`"
      >
        <svg width="100%" height="100%">
          <!-- gets display: none after translating -->
          <mask :id="`mask-${index}`" :style="maskStyle[index]">
            <image
              :xlink:href="card.images.puzzlePiece.full"
              :width="`${card.images.puzzlePiece.width + 11}%`"
              height="100%"
              :x="`${card.images.puzzlePiece.x - 6.5}%`"
              :y="`${card.images.puzzlePiece.y}%`"
            ></image>
          </mask>
          <image
            :xlink:href="card.images.puzzlePiece.full"
            :width="`${card.images.puzzlePiece.width + 11}%`"
            height="100%"
            :x="`${card.images.puzzlePiece.x - 6.5}%`"
            :y="`${card.images.puzzlePiece.y}%`"
          ></image>
          <!-- gets display: block after translating -->
          <svg width="100%" :mask="`url(#mask-${index})`" :style="svgStyle[index]">
            <image :xlink:href="card.images.letter" width="100%" height="100%"></image>
            <image :xlink:href="card.images.main" width="100%" height="100%" y="28%"></image>
          </svg>
        </svg>
      </figure>
    </div>
  </section>
</template>

<script>
import { TweenMax, TimelineLite, TweenLite, Power0 } from 'gsap';
import Draggable from 'gsap/Draggable';

import AssetLoader from 'mixins/AssetLoader';
import ErrorLogger from 'mixins/ErrorLogger';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

export default {
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger],

  data() {
    return {
      activityName: 'PuzzlePieces',
      sfx: null,
      roundSetupTime: 0.8,
      maskStyle: [],
      svgStyle: [],
      caption: {
        c0: false,
        c1: false,
        c2: false,
        c3: false,
        c4: false
      },
      isMatched: {
        m0: false,
        m1: false,
        m2: false,
        m3: false,
        m4: false
      }
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  methods: {
    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      this.sfx = await this.loadSounds();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Move the pieces to finish each puzzle.',
          voice: `${this.voiceInstructionsPrefix}/puzzlepieces-${this.getRandomVoice()}-1.mp3`,
          delay: 3
        }
      };
    },

    async loadImages() {
      this.rounds.forEach(round => {
        round.steps.forEach(({ images: { letter, main } }) => {
          this.queueImage(letter);
          this.queueImage(main);
        });
      });
      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(round => {
        round.steps.forEach(({ audio }) => {
          this.queueAudio(audio);
        });
      });

      const cache = await this.downloadAudio();

      return this.rounds.map(round => round.steps.map(({ audio }) => cache.get(audio)));
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();
      this.$nextTick(() => {
        const { length: stepsCount } = this.currentRound.steps;
        this.svgStyle = new Array(stepsCount).fill(0).map(() => ({}));
        this.maskStyle = new Array(stepsCount).fill(0).map(() => ({}));
        TweenMax.to('.activity-stage', this.roundSetupTime, { opacity: 1 });
        this.dragAndDrop();
      });
    },

    finishStep() {
      this.completeStep();
      this.setCurrentStep();
      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();
      if (!this.isEndOfActivity && !this.isLastRound) {
        timeline.delay(this.roundSetupTime).call(() => {
          this.setupNewRound();
          const figures = Array.from(document.querySelectorAll('.figure-card'));
          figures.forEach(card => TweenMax.set(card, { opacity: 1, transform: 'none' }));

          this.$nextTick(() => {
            const { length: stepsCount } = this.currentRound.steps;
            this.svgStyle = new Array(stepsCount).fill(0).map(() => ({}));
            this.maskStyle = new Array(stepsCount).fill(0).map(() => ({}));
            this.caption = {
              c0: false,
              c1: false,
              c2: false,
              c3: false,
              c4: false
            };
            this.isMatched = {
              m0: false,
              m1: false,
              m2: false,
              m3: false,
              m4: false
            };
            this.dragAndDrop();
          });
        });
      } else {
        timeline.delay(this.roundSetupTime + 0.5).call(this.completeActivity);
      }
    },

    handleDragEnd(dragContext, componentInstance, drag, drop, index) {
      const self = componentInstance;
      if (dragContext.hitTest(drop, '75%')) {
        const i = Number(dragContext.target.id.split('-')[1]);
        dragContext.applyBounds(drop);
        self.isMatched[`m${i}`] = true;
        self.caption[`c${i}`] = true;
        self.sfx[this.currentRound.roundIndex][i].play();
        self.sndBell();
        if (self.checkRoundEnd()) {
          self.handleRoundEnd();
        }
        self.maskStyle = self.maskStyle.map((style, sI) =>
          sI === index ? { display: 'none' } : style
        );
        self.svgStyle = self.svgStyle.map((style, sI) =>
          sI === index ? { display: 'block' } : style
        );
      } else {
        self.sndError();
        TweenLite.to(drag, self.roundSetupTime, {
          x: 0,
          y: 0,
          ease: Power0.easeNone
        });
      }
    },

    dragAndDrop() {
      const self = this;
      const { steps } = this.currentRound;
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      let bounds = null;

      steps.forEach((card, index) => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        const drag = document.getElementById(`drag-${index}`);
        const drop = document.getElementById(`drop-${index}`);

        Draggable.create(drag, {
          cursor: 'grab',
          throwProps: true,
          minimumMovement: 0,
          onPress() {
            if (bounds) return;
            bounds = getActivityBoundings();
          },
          onDrag() {
            // work-around for iOS not firing touchend on tablet
            if (!isMobile) return;
            clearTimeout(dragTimer);
            const { pointerX, pointerY } = this;
            const hasHitBoundings = hasTouchedBoundings(pointerX, pointerY, bounds);
            if (hasHitBoundings && !hasHitBoundary) {
              dragTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDragEnd(this, self, drag, drop);
              }, 200);
            }
          },
          onDragStart() {
            tLite.set(drag, { cursor: 'grabbing' });
            self.sndType();
          },
          onDragEnd() {
            this.vars.cursor = 'grab';
            clearTimeout(dragTimer);
            hasHitBoundary = false;
            self.handleDragEnd(this, self, drag, drop, index);
          }
        });
        // Draggable.get(drag).vars.cursor = 'grabbing';
      });
    },

    checkRoundEnd() {
      const maxRounds = this.activityData.numberOfCards;
      const values = Object.values(this.caption);
      for (let i = 0; i < maxRounds; i += 1) {
        if (!values[i]) {
          return false;
        }
      }
      return true;
    }
  }
};
</script>

<style lang="scss" scoped>
// Mixins and Extends

%cardsContainer {
  display: inline-flex;
  justify-content: space-evenly;
  height: 45%;
  width: 100%;
  margin: 0;
  padding: 0;
  @supports (-ms-ime-align: auto) {
    & {
      justify-content: space-around !important;
    }
  }

  figcaption {
    width: 100%;
    text-align: center;
  }
}

// CSS of the activity
section {
  .cards-container {
    @extend %cardsContainer;
    margin: 5% 0;
    figure {
      text-shadow: lighten(black, 50%) 0 0 20px;
      -webkit-box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
      -moz-box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
      box-shadow: 0px 0px 45px -4px rgba(0, 0, 0, 0.75);
    }
  }

  .puzzles-container {
    @extend %cardsContainer;
    flex-direction: row-reverse;
    margin-top: -1.5%;
  }

  figure {
    margin: 0;
    padding: 0;
  }

  .figure-type-3 {
    height: 95%;
    width: 22%;
  }

  .figure-type-4 {
    height: 88%;
    width: 20%;
  }

  .figure-type-5 {
    height: 90%;
    width: 17%;
  }
  @media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: landscape) and (-webkit-min-device-pixel-ratio: 2) {
    .figure-type-3 {
      width: 20%;
    }
    .figure-type-4 {
      width: 18.5%;
    }
  }
}
</style>
